import React, { useState, useEffect } from 'react';
import { X, Check, Mail, Trash, Plus, Copy, FileX, FolderPlus, ListX } from 'lucide-react';
import { updateProviderProfile, activateProvider, deactivateProvider, contactProvider, deleteProvider } from "../../services/providersApi";
import { deleteProviderFiles } from "../../storageService";
import { toast } from "react-toastify";
import Spinner from "../Spinner/Spinner";
import { addCategory as apiAddCategory } from "../../services/categoriesApi";
import { createLocation as apiCreateLocation } from "../../services/locationsApi";
import { useCategories } from "../../utils/CategoriesContext";
import { useLocations } from "../../utils/LocationContext";
import './ProviderData.css';
import './ProviderDataEnhanced.css'; // Importamos los nuevos estilos mejorados

const ProviderData = ({ provider, images, onClose, onProviderAction }) => {
    const { getCategoriesByType, refreshCategories } = useCategories();
    const { locationNames, refreshLocations } = useLocations();
    const [loading, setLoading] = useState(false);
    const [contactModalOpen, setContactModalOpen] = useState(false);
    const [contactMessage, setContactMessage] = useState("");
    const [editMode, setEditMode] = useState(false);

    // =================== NUEVOS ESTADOS CONTROLADOS PARA TODOS LOS CAMPOS ===================
    const [name, setName] = useState(provider.name || "");
    const [email, setEmail] = useState(provider.email || "");
    const [phone, setPhone] = useState(provider.phone || "");
    const [description, setDescription] = useState(provider.description || "");
    const [timetable, setTimetable] = useState(provider.timetable || "");

    // Contadores de clics - Nuevos campos
    const [phoneClicks, setPhoneClicks] = useState(provider.phoneClicks || 0);
    const [linkClicks, setLinkClicks] = useState(provider.linkClicks || {});

    // Específicos por tipo
    const [document, setDocument] = useState(provider.document || "");
    const [documentName, setDocumentName] = useState(provider.documentName || "");
    const [certificates, setCertificates] = useState(Array.isArray(provider.certificates) ? provider.certificates : []);
    const [cuit, setCuit] = useState(provider.cuit || "");
    const [branches, setBranches] = useState(Array.isArray(provider.branches) ? provider.branches : []);

    // Adicionales
    const [keywords, setKeywords] = useState(Array.isArray(provider.keywords) ? provider.keywords : []);
    const [links, setLinks] = useState(Array.isArray(provider.links) ? provider.links.map(l => typeof l === 'string' ? { url: l } : l) : []);

    // Estado para los campos editables EXISTENTES (ahora sincronizados con los controlados cuando sea necesario)
    const [categories, setCategories] = useState(provider.categoryIds || []); // ahora IDs
    const [subcategories, setSubcategories] = useState(provider.subcategories || []);
    const [locations, setLocations] = useState(provider.locations || []);

    // Estados auxiliares para inputs de lista simples
    const [newCertificate, setNewCertificate] = useState("");
    const [newBranch, setNewBranch] = useState("");
    const [newKeyword, setNewKeyword] = useState("");

    // =================== DISPONIBLES / VALIDACIÓN ===================
    const [availableCategories, setAvailableCategories] = useState([]); // [{name, type, ...}]
    const [availableLocations, setAvailableLocations] = useState([]); // ["La Plata", ...]
    const [availableSubcategories, setAvailableSubcategories] = useState([]); // ["Categoria-Subcat", ...]

    // Mapa id->objeto categoría para acceso rápido (debe ir antes de useEffect que lo usa)
    const categoriesMap = React.useMemo(() => {
        const map = {};
        (availableCategories || []).forEach(cat => { if (cat?.id) map[cat.id] = cat; });
        return map;
    }, [availableCategories]);

    // Eliminamos estados no usados (validaciones y sugerencias)
    // const [categoryValidation, setCategoryValidation] = useState({ isValid: true, message: "" });
    // const [subcategoryValidation, setSubcategoryValidation] = useState({ isValid: true, message: "" });
    // const [locationValidation, setLocationValidation] = useState({ isValid: true, message: "" });

    // const [categorySuggestions, setCategorySuggestions] = useState([]);
    // const [locationSuggestions, setLocationSuggestions] = useState([]);
    // const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
    // const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

    // Removemos refs no usados
    // const categoryInputRef = useRef(null);
    // const locationInputRef = useRef(null);

    // Estados para crear nueva categoría desde modal
    const [showCreateCategory, setShowCreateCategory] = useState(false);
    const [createCategoryData, setCreateCategoryData] = useState({ name: '', type: '' });
    const [createCategoryError, setCreateCategoryError] = useState('');
    const [createCategoryLoading, setCreateCategoryLoading] = useState(false);

    // Estados para crear nueva location desde modal
    const [showCreateLocation, setShowCreateLocation] = useState(false);
    const [createLocationData, setCreateLocationData] = useState({
        name: '',
        zone: '',
        code: '',
        priority: 1,
        centerCoordinates: { lat: 0, lng: 0 },
        radiusMeters: 1000
    });
    const [createLocationError, setCreateLocationError] = useState('');
    const [createLocationLoading, setCreateLocationLoading] = useState(false);

    // =============== Helpers de reseteo ===============
    const resetStateFromProvider = (prov) => {
        setName(prov.name || "");
        setEmail(prov.email || "");
        setPhone(prov.phone || "");
        setDescription(prov.description || "");
        setTimetable(prov.timetable || "");
        // Actualizar estados de contadores
        setPhoneClicks(prov.phoneClicks || 0);
        setLinkClicks(prov.linkClicks || {});

        setDocument(prov.document || "");
        setDocumentName(prov.documentName || "");
        setCertificates(Array.isArray(prov.certificates) ? prov.certificates : []);
        setCuit(prov.cuit || "");
        setBranches(Array.isArray(prov.branches) ? prov.branches : []);
        setKeywords(Array.isArray(prov.keywords) ? prov.keywords : []);
        setLinks(Array.isArray(prov.links) ? prov.links.map(l => typeof l === 'string' ? { url: l } : l) : []);

        setCategories(prov.categoryIds || []); // ajustar a IDs
        setSubcategories(prov.subcategories || []);
        setLocations(prov.locations || []);
    };

    useEffect(() => {
        // Cuando cambia el proveedor de props, reseteamos los estados controlados
        resetStateFromProvider(provider);
    }, [provider]);

    // Datos disponibles desde los contexts (prefetch en login/app start).
    // Se sincronizan automáticamente cuando llega el catálogo o cambia el tipo.
    useEffect(() => {
        setAvailableCategories(getCategoriesByType(provider.type) || []);
    }, [provider.type, getCategoriesByType]);

    useEffect(() => {
        setAvailableLocations(locationNames || []);
    }, [locationNames]);

    // Cargar subcategorías disponibles en base a categorías seleccionadas y tipo
    useEffect(() => {
        // Esperar a que las categorías disponibles estén cargadas para evitar borrar subcategorías iniciales
        if (!availableCategories.length) return;
        if (!categories || categories.length === 0) {
            setAvailableSubcategories([]);
            return;
        }
        const subs = [];
        categories.forEach(catId => {
            const catObj = categoriesMap[catId];
            if (catObj?.subcategories?.length) {
                catObj.subcategories.forEach(sub => subs.push(`${catId}-${sub}`));
            }
        });
        const flattened = Array.from(new Set(subs));
        setAvailableSubcategories(flattened);
        // Si ya tenemos subcategorías cargadas desde el proveedor y aún no hemos sincronizado (están vacías), restauramos considerando intersección.
        setSubcategories(prev => {
            if (prev.length === 0 && (provider.subcategories || []).length) {
                // Mantener solo las que existan en flattened
                return (provider.subcategories || []).filter(sc => flattened.includes(sc));
            }
            return prev.filter(sc => flattened.includes(sc));
        });
    }, [categories, categoriesMap, availableCategories, provider.subcategories]);


    // =================== VALIDACIONES (solo para crear) ===================
    const containsSuggestion = (arr) => {
        return arr.some(item => item.includes('sugerencia'));
    };

    // Activar/desactivar proveedor
    const handleActivateProvider = async () => {
        if (containsSuggestion(categories) || containsSuggestion(subcategories) || containsSuggestion(locations)) {
            toast.error("No se puede activar el proveedor. Elimina los ítems con '(sugerencia)' en Categorías, Subcategorías o Ubicaciones.");
            return;
        }
        try {
            setLoading(true);
            await activateProvider(provider.username);
            toast.success("Proveedor activado");
            onProviderAction('activate', provider.username);
        } catch (err) {
            console.error(err);
            // El backend devuelve el motivo concreto (localidad/categoría/subcategoría) en el body
            const backendMsg = typeof err?.response?.data === 'string' ? err.response.data : null;
            toast.error(backendMsg || "Error al activar el proveedor.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeactivateProvider = async () => {
        try {
            setLoading(true);
            await deactivateProvider(provider.username);
            toast.success("Proveedor desactivado");
            onProviderAction('deactivate', provider.username);
        } catch (err) {
            console.error(err);
            toast.error("Error al desactivar el proveedor.");
        } finally {
            setLoading(false);
        }
    };

    // Eliminar proveedor
    const handleProviderDelete = async () => {
        if (!window.confirm("¿Estás seguro de que quieres eliminar este proveedor? Esta acción no se puede deshacer.")) {
            return;
        }

        setLoading(true);
        try {
            await deleteProviderFiles(provider.type, provider.username);
            await deleteProvider(provider.username, provider.type);
            toast.success("Proveedor eliminado");
            onProviderAction('delete', provider.username);
        } catch (err) {
            console.error(err);
            toast.error("Error al eliminar el proveedor.");
        } finally {
            setLoading(false);
        }
    };

    // Contactar proveedor
    const handleSendContactMessage = async () => {
        if (!contactMessage.trim()) {
            toast.error("Escribe un mensaje para el proveedor.");
            return;
        }

        try {
            setLoading(true);
            await contactProvider(provider.username, {
                email: provider.email,
                message: contactMessage
            });
            toast.success("Mensaje enviado correctamente");
            setContactModalOpen(false);
            setContactMessage("");
        } catch (err) {
            console.error(err);
            toast.error("Error al enviar el mensaje.");
        } finally {
            setLoading(false);
        }
    };

    // =================== Crear categoría/ubicación ===================
    const handleCreateCategory = async () => {
        setCreateCategoryError('');
        const { name, type } = createCategoryData;
        if (!name || !type) {
            setCreateCategoryError('Nombre y Tipo son obligatorios.');
            return;
        }
        setCreateCategoryLoading(true);
        try {
            const resp = await apiAddCategory(createCategoryData);
            toast.success('Categoría creada');
            // Refrescamos el catálogo global para que todos los consumidores lo vean.
            refreshCategories();
            if (resp?.id) {
                setCategories(prev => Array.from(new Set([...(prev || []), resp.id])));
            }
            setShowCreateCategory(false);
            setCreateCategoryData({ name: '', type: '' });
        } catch (err) {
            setCreateCategoryError('Error al crear categoría.');
        } finally {
            setCreateCategoryLoading(false);
        }
    };

    const handleCreateLocation = async () => {
        setCreateLocationError('');
        const { name, zone, code } = createLocationData;
        if (!name || !zone || !code) {
            setCreateLocationError('Nombre, Zona y Código son obligatorios.');
            return;
        }
        setCreateLocationLoading(true);
        try {
            const resp = await apiCreateLocation(createLocationData);
            toast.success('Location creada');

            refreshLocations();
            setLocations(prev => Array.from(new Set([...(prev || []), resp.name || name])));

            setShowCreateLocation(false);
            setCreateLocationData({
                name: '',
                zone: '',
                code: '',
                priority: 1,
                centerCoordinates: { lat: 0, lng: 0 },
                radiusMeters: 1000
            });
        } catch (err) {
            setCreateLocationError('Error al crear Location.');
        } finally {
            setCreateLocationLoading(false);
        }
    };

    // Guardar cambios
    const handleSaveChanges = async () => {
        try {
            setLoading(true);

            const updatedData = {
                ...provider,
                name,
                email,
                phone,
                description,
                timetable,
                phoneClicks,
                linkClicks,
                document,
                documentName,
                certificates,
                cuit,
                branches,
                keywords,
                links,
                // IDs de categorías
                categoryIds: categories,
                // También enviar en 'categories' por compatibilidad requerida
                categories: categories,
                subcategories,
                locations
            };

            // LOG: payload antes de enviar (incluye ambos campos)
            console.log('[ProviderData] handleSaveChanges payload', {
                username: provider.username,
                type: provider.type,
                categoryIds: categories,
                categoriesDup: categories,
                resolvedCategories: categories.map(id => ({ id, name: categoriesMap[id]?.name })),
                subcategories,
                locations,
                updatedData
            });

            await updateProviderProfile(provider.username, provider.type, updatedData);
            console.log('[ProviderData] updateProviderProfile success');
            toast.success("Información del proveedor actualizada correctamente");
            setEditMode(false);
            onProviderAction('update', provider.username, updatedData);
        } catch (err) {
            console.error('[ProviderData] updateProviderProfile error', err);
            toast.error("Error al actualizar la información del proveedor.");
        } finally {
            setLoading(false);
        }
    };

    // Helpers para renderizado de visualización
    const displayIf = (label, val, isArr = false) => {
        if (!val || (isArr && !val.length)) return null;
        return (
            <p><strong>{label}:</strong> {isArr ? val.join(', ') : val}</p>
        );
    };

    // Render de chips simples
    const ChipsEditor = ({ title, items, onRemove, newValue, setNewValue, onAdd, placeholder }) => (
        <div className="edit-card">
            <h3>{title}</h3>
            <div className="editable-array-items">
                {(items || []).map((item, idx) => (
                    <div key={idx} className={`editable-array-item`}>
                        <span>{item}</span>
                        <button className="remove-item-btn" onClick={() => onRemove(item)} title={`Eliminar ${item}`}>
                            <X size={14} />
                        </button>
                    </div>
                ))}
            </div>
            <div className="add-item-container">
                <input
                    type="text"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder={placeholder}
                    className="add-item-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') onAdd(); }}
                />
                <button className="add-item-btn" onClick={onAdd} title="Agregar">
                    <Plus size={18} />
                    Agregar
                </button>
            </div>
        </div>
    );

    // =============== UI ===============
    return (
        <div className="provider-fullscreen-view">
            {loading && <Spinner />}

            <div className="provider-content-wrapper">
                {/* Header fijo con botones de acción */}
                <div className="provider-header">
                    <div className="provider-header-left">
                        <button
                            className="back-btn"
                            onClick={onClose}
                            title="Volver"
                        >
                            ← Volver
                        </button>
                        <h2 className="provider-title">{provider.name}</h2>
                        <span className={`provider-status ${provider.active ? 'active' : 'inactive'}`}>
                {provider.active ? 'Activo' : 'Inactivo'}
              </span>
                    </div>

                    <div className="provider-header-actions">
                        {editMode ? (
                            <>
                                <button
                                    className="action-btn save-btn"
                                    onClick={handleSaveChanges}
                                    disabled={loading}
                                >
                                    <Check size={16}/>
                                    Guardar
                                </button>
                                <button
                                    className="action-btn cancel-btn"
                                    onClick={() => { resetStateFromProvider(provider); setEditMode(false); }}
                                >
                                    <X size={16}/>
                                    Cancelar
                                </button>
                            </>
                        ) : (
                            <button
                                className="action-btn edit-btn"
                                onClick={() => setEditMode(true)}
                            >
                                Editar
                            </button>
                        )}
                    </div>
                </div>

                {/* Barra de acciones secundarias */}
                <div className="provider-actions-bar">
                    {provider.active ? (
                        <button
                            className="action-btn deactivate-btn"
                            onClick={handleDeactivateProvider}
                        >
                            <X size={16} />
                            Desactivar
                        </button>
                    ) : (
                        <button
                            className="action-btn activate-btn"
                            onClick={handleActivateProvider}
                        >
                            <Check size={16} />
                            Activar
                        </button>
                    )}
                    <button
                        className="action-btn contact-btn"
                        onClick={() => setContactModalOpen(true)}
                    >
                        <Mail size={16} />
                        Contactar
                    </button>
                    <button
                        className="action-btn delete-btn"
                        onClick={handleProviderDelete}
                    >
                        <Trash size={16} />
                        Eliminar
                    </button>
                    <button
                        className="action-btn duplicate-btn"
                        onClick={() => onProviderAction('duplicate', provider.username, provider)}
                        title="Duplicar proveedor"
                    >
                        <Copy size={16} />
                        Duplicar
                    </button>
                </div>

                {/* Contenido principal en scroll */}
                <div className="provider-main-content">
                    <div className="provider-content-grid">
                        {/* Columna izquierda - Datos básicos y categorías */}
                        <div className="provider-info-section">
                            <div className="info-card">
                                <h3>Información Básica</h3>
                                {editMode ? (
                                    <div className="form-grid">
                                        <label>
                                            <span>Nombre</span>
                                            <input className="form-input" type="text" value={name} onChange={(e)=>setName(e.target.value)} />
                                        </label>
                                        <label>
                                            <span>Email</span>
                                            <input className="form-input" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} />
                                        </label>
                                        <label>
                                            <span>Teléfono</span>
                                            <input className="form-input" type="text" value={phone} onChange={(e)=>setPhone(e.target.value)} />
                                        </label>
                                        <label className="col-span-2">
                                            <span>Descripción</span>
                                            {/* Textarea mejorado con las nuevas clases para tema claro/oscuro */}
                                            <textarea
                                                className="cm-provider-enhanced-textarea"
                                                rows={6}
                                                value={description}
                                                onChange={(e)=>setDescription(e.target.value)}
                                            />
                                        </label>
                                        <label className="col-span-2">
                                            <span>Horario</span>
                                            <input className="form-input" type="text" value={timetable} onChange={(e)=>setTimetable(e.target.value)} />
                                        </label>
                                    </div>
                                ) : (
                                    <div className="info-grid">
                                        {displayIf("Username", provider.username)}
                                        {displayIf("Email", provider.email)}
                                        {displayIf("Teléfono", provider.phone)}
                                        {displayIf("Descripción", provider.description)}
                                        {displayIf("Horario", provider.timetable)}
                                        {provider.creationDate && (
                                            <div className="info-item">
                                                <strong>Creado:</strong> {new Date(provider.creationDate.seconds * 1000).toLocaleDateString()}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Información específica por tipo */}
                            {provider.type === "service" && (
                                <div className="info-card">
                                    <h3>Datos de Servicio</h3>
                                    {editMode ? (
                                        <div className="form-grid">
                                            <label>
                                                <span>Documento</span>
                                                <input className="form-input" type="text" value={document} onChange={(e)=>setDocument(e.target.value)} />
                                            </label>
                                            <label>
                                                <span>CUIT</span>
                                                <input className="form-input" type="text" value={cuit} onChange={(e)=>setCuit(e.target.value)} />
                                            </label>
                                            <label>
                                                <span>Nombre Completo</span>
                                                <input className="form-input" type="text" value={documentName} onChange={(e)=>setDocumentName(e.target.value)} />
                                            </label>
                                            <div className="col-span-2">
                                                <ChipsEditor
                                                    title="Certificados"
                                                    items={certificates}
                                                    onRemove={(item)=>setCertificates(certificates.filter(c=>c!==item))}
                                                    newValue={newCertificate}
                                                    setNewValue={setNewCertificate}
                                                    onAdd={()=>{ if(newCertificate.trim()){ setCertificates([...certificates, newCertificate.trim()]); setNewCertificate(''); } }}
                                                    placeholder="Agregar certificado"
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="info-grid">
                                            {displayIf("Documento", provider.document)}
                                            {displayIf("CUIT", provider.cuit)}
                                            {displayIf("Nombre Completo", provider.documentName)}
                                            {displayIf("Certificados", provider.certificates, true)}
                                        </div>
                                    )}
                                </div>
                            )}

                            {provider.type === "products" && (
                                <div className="info-card">
                                    <h3>Datos de Productos</h3>
                                    {editMode ? (
                                        <div className="form-grid">
                                            <div className="col-span-2">
                                                <ChipsEditor
                                                    title="Sucursales"
                                                    items={branches}
                                                    onRemove={(item)=>setBranches(branches.filter(b=>b!==item))}
                                                    newValue={newBranch}
                                                    setNewValue={setNewBranch}
                                                    onAdd={()=>{ if(newBranch.trim()){ setBranches([...branches, newBranch.trim()]); setNewBranch(''); } }}
                                                    placeholder="Agregar sucursal"
                                                />
                                            </div>
                                            <label>
                                                <span>CUIT</span>
                                                <input className="form-input" type="text" value={cuit} onChange={(e)=>setCuit(e.target.value)} />
                                            </label>
                                        </div>
                                    ) : (
                                        <div className="info-grid">
                                            {displayIf("Sucursales", provider.branches, true)}
                                            {displayIf("CUIT", provider.cuit)}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="info-card">
                                <h3>Información Adicional</h3>
                                {editMode ? (
                                    <div className="form-grid">
                                        <div className="col-span-2">
                                            <ChipsEditor
                                                title="Palabras clave"
                                                items={keywords}
                                                onRemove={(item)=>setKeywords(keywords.filter(k=>k!==item))}
                                                newValue={newKeyword}
                                                setNewValue={setNewKeyword}
                                                onAdd={()=>{ if(newKeyword.trim()){ setKeywords([...keywords, newKeyword.trim()]); setNewKeyword(''); } }}
                                                placeholder="Agregar palabra clave"
                                            />
                                        </div>
                                        <div className="col-span-2 edit-card">
                                            <h3>Links</h3>
                                            <div className="cm-provider-links-container">
                                                {(links || []).map((link, idx) => (
                                                    <div key={idx} className="cm-provider-link-row">
                                                        {/* Campo para nombre del link */}
                                                        <div className="cm-provider-link-field cm-provider-link-field-name">
                                                            <input
                                                                className="form-input"
                                                                type="text"
                                                                value={link.name || ''}
                                                                onChange={(e)=>{
                                                                    const nu = [...links];
                                                                    nu[idx] = { ...nu[idx], name: e.target.value };
                                                                    setLinks(nu);
                                                                }}
                                                                placeholder="Nombre del link"
                                                            />
                                                        </div>
                                                        <div className="cm-provider-link-field">
                                                            <input
                                                                className="form-input"
                                                                type="url"
                                                                value={link.url || ''}
                                                                onChange={(e)=>{
                                                                    const nu = [...links];
                                                                    nu[idx] = { ...nu[idx], url: e.target.value };
                                                                    setLinks(nu);
                                                                }}
                                                                placeholder="https://..."
                                                            />
                                                        </div>
                                                        <button className="cm-provider-link-remove-btn" onClick={()=> setLinks(links.filter((_,i)=>i!==idx))}>
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                ))}
                                                <button
                                                    className="cm-provider-link-add-btn"
                                                    onClick={()=>{ setLinks([...links, {name: '', url: ''}]); }}
                                                >
                                                    <Plus size={18} /> Agregar Link
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="info-grid">
                                        {displayIf("Palabras clave", provider.keywords, true)}
                                        {/* Mostrar links con nombres si existen */}
                                        {links && links.length > 0 && (
                                            <p>
                                                <strong>Links:</strong>{' '}
                                                {links.map((l,i) => (
                                                    <React.Fragment key={i}>
                                                        <a href={l.url} target="_blank" rel="noreferrer">
                                                            {l.name || l.url}
                                                        </a>
                                                        {i<links.length-1 && ', '}
                                                    </React.Fragment>
                                                ))}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Sección de Estadísticas de Clics */}
                            <div className="info-card">
                                <h3>Estadísticas de Contacto</h3>
                                <div className="info-grid">
                                    <div className="info-item">
                                        <strong>Clics en WhatsApp:</strong> {phoneClicks || 0}
                                    </div>
                                    {Object.keys(linkClicks || {}).length > 0 ? (
                                        <div className="info-item">
                                            <strong>Clics en Links:</strong>
                                            <ul className="clicks-list">
                                                {Object.entries(linkClicks).map(([linkName, count]) => (
                                                    <li key={linkName}>
                                                        <span>{linkName}: </span>
                                                        <strong>{count}</strong>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ) : (
                                        <div className="info-item">
                                            <strong>Clics en Links:</strong> No hay datos
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Categorías y ubicaciones */}
                            {editMode ? (
                                <>
                                    {/* Selector dual para Categorías */}
                                    <div className="info-card">
                                        <div className="cm-provider-selector-dual">
                                            <h3 className="cm-provider-selector-title">Categorías</h3>
                                            <div className="cm-provider-selector-container">
                                                <div className="cm-provider-selector-column">
                                                    <div className="cm-provider-selector-header cm-provider-selector-header-selected">
                                                        Seleccionadas
                                                        <span className="cm-provider-selector-count">{categories.length}</span>
                                                    </div>
                                                    <div className="cm-provider-selector-list">
                                                        {categories.length > 0 ? (
                                                            categories.map((catId) => {
                                                                const catObj = categoriesMap[catId];
                                                                return (
                                                                    <div
                                                                        key={catId}
                                                                        className="cm-provider-selector-item"
                                                                    >
                                                                        <div className="cm-provider-selector-item-name">
                                                                            {catObj ? catObj.name : catId} <small>({catId})</small>
                                                                        </div>
                                                                        <button
                                                                            className="cm-provider-selector-item-button cm-provider-selector-remove-button"
                                                                            onClick={() => {
                                                                                const next = categories.filter((c) => c !== catId);
                                                                                console.log('[ProviderData] remove category', { removed: catId, name: categoriesMap[catId]?.name, next });
                                                                                setCategories(next);
                                                                                setSubcategories(subcategories.filter(sc => !sc.startsWith(catId + '-')));
                                                                            }}
                                                                            title="Quitar"
                                                                        >
                                                                            <X size={14} />
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })
                                                        ) : (
                                                            <div className="cm-provider-selector-empty-message">
                                                                <FileX size={24} />
                                                                No hay categorías seleccionadas
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="cm-provider-selector-column">
                                                    <div className="cm-provider-selector-header cm-provider-selector-header-available">
                                                        Disponibles
                                                        <span className="cm-provider-selector-count">
                                  {availableCategories.filter(cat => !categories.includes(cat.id)).length}
                                </span>
                                                    </div>
                                                    <div className="cm-provider-selector-list">
                                                        {availableCategories
                                                            .filter(cat => !categories.includes(cat.id))
                                                            .map(cat => (
                                                                <div
                                                                    key={cat.id}
                                                                    className="cm-provider-selector-item"
                                                                >
                                                                    <div className="cm-provider-selector-item-name">{cat.name} <small>({cat.id})</small></div>
                                                                    <button
                                                                        className="cm-provider-selector-item-button cm-provider-selector-add-button"
                                                                        onClick={() => {
                                                                            const next = [...categories, cat.id];
                                                                            console.log('[ProviderData] add category', { added: cat.id, name: cat.name, next });
                                                                            setCategories(next);
                                                                        }}
                                                                        title="Agregar"
                                                                    >
                                                                        <Plus size={14} />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        {availableCategories.length === 0 && (
                                                            <div className="cm-provider-selector-empty-message">
                                                                <ListX size={24} />
                                                                No hay categorías disponibles
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                className="cm-provider-selector-create-button"
                                                onClick={() => setShowCreateCategory(true)}
                                            >
                                                <Plus size={16} /> Crear nueva categoría
                                            </button>
                                        </div>
                                    </div>

                                    {/* Selector dual para Subcategorías */}
                                    <div className="info-card">
                                        <div className="cm-provider-selector-dual">
                                            <h3 className="cm-provider-selector-title">Subcategorías</h3>
                                            <div className="cm-provider-selector-container">
                                                <div className="cm-provider-selector-column">
                                                    <div className="cm-provider-selector-header cm-provider-selector-header-selected">
                                                        Seleccionadas
                                                        <span className="cm-provider-selector-count">{subcategories.length}</span>
                                                    </div>
                                                    <div className="cm-provider-selector-list">
                                                        {subcategories.length > 0 ? (
                                                            subcategories.map((subcat) => {
                                                                const lastDash = subcat.lastIndexOf('-');
                                                                const catId = lastDash !== -1 ? subcat.substring(0, lastDash) : subcat;
                                                                const sub = lastDash !== -1 ? subcat.substring(lastDash + 1) : '';
                                                                const catObj = categoriesMap[catId];
                                                                return (
                                                                    <div
                                                                        key={subcat}
                                                                        className="cm-provider-selector-item"
                                                                    >
                                                                        <div className="cm-provider-selector-item-name">
                                                                            {sub} <small>({catObj ? catObj.name : catId} / {catId})</small>
                                                                        </div>
                                                                        <button
                                                                            className="cm-provider-selector-item-button cm-provider-selector-remove-button"
                                                                            onClick={() => {
                                                                                const next = subcategories.filter((s) => s !== subcat);
                                                                                console.log('[ProviderData] remove subcategory', { removed: subcat, next });
                                                                                setSubcategories(next);
                                                                            }}
                                                                            title="Quitar"
                                                                        >
                                                                            <X size={14} />
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })
                                                        ) : (
                                                            <div className="cm-provider-selector-empty-message">
                                                                <FileX size={24} />
                                                                No hay subcategorías seleccionadas
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="cm-provider-selector-column">
                                                    <div className="cm-provider-selector-header cm-provider-selector-header-available">
                                                        Disponibles
                                                        <span className="cm-provider-selector-count">
                                  {availableSubcategories.filter(sc => !subcategories.includes(sc)).length}
                                </span>
                                                    </div>
                                                    <div className="cm-provider-selector-list">
                                                        {availableSubcategories
                                                            .filter(sc => !subcategories.includes(sc))
                                                            .map((subcat) => {
                                                                const lastDash = subcat.lastIndexOf('-');
                                                                const catId = lastDash !== -1 ? subcat.substring(0, lastDash) : subcat;
                                                                const sub = lastDash !== -1 ? subcat.substring(lastDash + 1) : '';
                                                                const catObj = categoriesMap[catId];
                                                                return (
                                                                    <div
                                                                        key={subcat}
                                                                        className="cm-provider-selector-item"
                                                                    >
                                                                        <div className="cm-provider-selector-item-name">
                                                                            {sub} <small>({catObj ? catObj.name : catId} / {catId})</small>
                                                                        </div>
                                                                        <button
                                                                            className="cm-provider-selector-item-button cm-provider-selector-add-button"
                                                                            onClick={() => {
                                                                                const next = [...subcategories, subcat];
                                                                                console.log('[ProviderData] add subcategory', { added: subcat, next });
                                                                                setSubcategories(next);
                                                                            }}
                                                                            title="Agregar"
                                                                        >
                                                                            <Plus size={14} />
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })}
                                                        {categories.length === 0 && (
                                                            <div className="cm-provider-selector-empty-message">
                                                                <FolderPlus size={24} />
                                                                Selecciona primero una categoría
                                                            </div>
                                                        )}
                                                        {categories.length > 0 && availableSubcategories.filter(sc => !subcategories.includes(sc)).length === 0 && (
                                                            <div className="cm-provider-selector-empty-message">
                                                                <ListX size={24} />
                                                                No hay subcategorías disponibles
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Selector dual para Ubicaciones */}
                                    <div className="info-card">
                                        <div className="cm-provider-selector-dual">
                                            <h3 className="cm-provider-selector-title">Ubicaciones</h3>
                                            <div className="cm-provider-selector-container">
                                                <div className="cm-provider-selector-column">
                                                    <div className="cm-provider-selector-header cm-provider-selector-header-selected">
                                                        Seleccionadas
                                                        <span className="cm-provider-selector-count">{locations.length}</span>
                                                    </div>
                                                    <div className="cm-provider-selector-list">
                                                        {locations.length > 0 ? (
                                                            locations.map((loc) => (
                                                                <div
                                                                    key={loc}
                                                                    className="cm-provider-selector-item"
                                                                >
                                                                    <div className="cm-provider-selector-item-name">{loc}</div>
                                                                    <button
                                                                        className="cm-provider-selector-item-button cm-provider-selector-remove-button"
                                                                        onClick={() => setLocations(locations.filter((l) => l !== loc))}
                                                                        title="Quitar"
                                                                    >
                                                                        <X size={14} />
                                                                    </button>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="cm-provider-selector-empty-message">
                                                                <FileX size={24} />
                                                                No hay ubicaciones seleccionadas
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="cm-provider-selector-column">
                                                    <div className="cm-provider-selector-header cm-provider-selector-header-available">
                                                        Disponibles
                                                        <span className="cm-provider-selector-count">
                                  {(availableLocations || []).filter(loc => !locations.includes(loc)).length}
                                </span>
                                                    </div>
                                                    <div className="cm-provider-selector-list">
                                                        {(availableLocations || [])
                                                            .filter(loc => !locations.includes(loc))
                                                            .map((loc) => (
                                                                <div
                                                                    key={loc}
                                                                    className="cm-provider-selector-item"
                                                                >
                                                                    <div className="cm-provider-selector-item-name">{loc}</div>
                                                                    <button
                                                                        className="cm-provider-selector-item-button cm-provider-selector-add-button"
                                                                        onClick={() => setLocations([...locations, loc])}
                                                                        title="Agregar"
                                                                    >
                                                                        <Plus size={14} />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                className="cm-provider-selector-create-button"
                                                onClick={() => setShowCreateLocation(true)}
                                            >
                                                <Plus size={16} /> Crear nueva ubicación
                                            </button>
                                        </div>
                                    </div>

                                    {/* Formularios de creación (se mantienen) */}
                                    {showCreateCategory && (
                                        <div className="edit-card create-form">
                                            <form onSubmit={e=>{e.preventDefault(); handleCreateCategory();}}>
                                                <div className="form-row">
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        value={createCategoryData.name}
                                                        onChange={e=>setCreateCategoryData(prev=>({...prev,name:e.target.value}))}
                                                        placeholder="Nombre de la categoría"
                                                    />
                                                    <select
                                                        className="form-select"
                                                        value={createCategoryData.type}
                                                        onChange={e=>setCreateCategoryData(prev=>({...prev,type:e.target.value}))}
                                                    >
                                                        <option value="" disabled>Tipo</option>
                                                        <option value="service">Service</option>
                                                        <option value="products">Products</option>
                                                    </select>
                                                </div>
                                                {createCategoryError && <div className="error-text">{createCategoryError}</div>}
                                                <div className="form-actions">
                                                    <button type="submit" className="action-btn save-btn" disabled={createCategoryLoading}>
                                                        {createCategoryLoading ? 'Creando...' : 'Crear'}
                                                    </button>
                                                    <button type="button" className="action-btn cancel-btn" onClick={()=>setShowCreateCategory(false)}>
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    )}

                                    {showCreateLocation && (
                                        <div className="edit-card create-form">
                                            <form onSubmit={e=>{e.preventDefault(); handleCreateLocation();}}>
                                                <div className="form-grid">
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        value={createLocationData.name}
                                                        onChange={e=>setCreateLocationData(prev=>({...prev,name:e.target.value}))}
                                                        placeholder="Nombre"
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        value={createLocationData.zone}
                                                        onChange={e=>setCreateLocationData(prev=>({...prev,zone:e.target.value}))}
                                                        placeholder="Zona"
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        value={createLocationData.code}
                                                        onChange={e=>setCreateLocationData(prev=>({...prev,code:e.target.value}))}
                                                        placeholder="Código"
                                                    />
                                                </div>
                                                {createLocationError && <div className="error-text">{createLocationError}</div>}
                                                <div className="form-actions">
                                                    <button type="submit" className="action-btn save-btn" disabled={createLocationLoading}>
                                                        {createLocationLoading ? 'Creando...' : 'Crear'}
                                                    </button>
                                                    <button type="button" className="action-btn cancel-btn" onClick={()=>setShowCreateLocation(false)}>
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="info-card">
                                    <h3>Categorías y Ubicaciones</h3>
                                    <div className="info-grid">
                                        {displayIf("Categorías", categories.map(id => {
                                            const c = categoriesMap[id];
                                            return c ? `${c.name} (${id})` : id;
                                        }), true)}
                                        {displayIf("Subcategorías", subcategories.map(sc => {
                                            const lastDash = sc.lastIndexOf('-');
                                            const catId = lastDash !== -1 ? sc.substring(0, lastDash) : sc;
                                            const sub = lastDash !== -1 ? sc.substring(lastDash + 1) : '';
                                            const catObj = categoriesMap[catId];
                                            return `${sub} (${catObj ? catObj.name : catId} / ${catId})`;
                                        }), true)}
                                        {displayIf("Ubicaciones", locations, true)}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Columna derecha - Fotos */}
                        <div className="provider-photos-section">
                            {images.profilePhoto && (
                                <div className="photo-card">
                                    <h3>Foto de Perfil</h3>
                                    <img
                                        src={images.profilePhoto}
                                        alt="Perfil"
                                        className="profile-image"
                                    />
                                </div>
                            )}

                            {images.documentPhoto && (
                                <div className="photo-card">
                                    <h3>Documento</h3>
                                    <p className="document-info">{provider.documentName} - {provider.document}</p>
                                    <img
                                        src={images.documentPhoto}
                                        alt="Documento"
                                        className="document-image"
                                    />
                                </div>
                            )}

                            {images.workPhotos?.length > 0 && (
                                <div className="photo-card">
                                    <h3>Fotos de Trabajos</h3>
                                    <div className="work-photos-grid">
                                        {images.workPhotos.map((url, i) => (
                                            <img
                                                key={i}
                                                src={url}
                                                alt={`Trabajo ${i + 1}`}
                                                className="work-image"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de contacto simplificado */}
            {contactModalOpen && (
                <div className="overlay">
                    <div className="contact-modal">
                        <div className="modal-header">
                            <h3>Enviar mensaje a {provider.name}</h3>
                            <button
                                className="close-btn"
                                onClick={() => setContactModalOpen(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="modal-body">
                  <textarea
                      rows={4}
                      value={contactMessage}
                      onChange={e => setContactMessage(e.target.value)}
                      placeholder="Escribí tu mensaje..."
                      className="contact-textarea"
                  />
                        </div>

                        <div className="modal-footer">
                            <button
                                className="action-btn cancel-btn"
                                onClick={() => setContactModalOpen(false)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="action-btn save-btn"
                                onClick={handleSendContactMessage}
                                disabled={!contactMessage.trim()}
                            >
                                Enviar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProviderData;
